import {
  pdf,
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  Font,
} from "@react-pdf/renderer";
import { useTranslate } from "@/locales";
import { useLocale } from "@/middleware";
import moment from "moment";
interface Offer {
  title: string;
  minValue: number;
  value: number;
  endDate: string;
  deliverDate: string;
  type: string;
  status: string;
  categories: string[];
  region: string;
  city: string;
  fileName: string;
  fileDescription: string;
  attachmentName: string;
  attachmentDescription: string;
  receiveDocumentsType: string;
  contactInfo: string;
  products: Product[];
  company?: CompanySchema; // Made optional for safety
}

interface Product {
  item: string;
  quantity: number;
  unitPrice?: number;
  tax?: number;
  attachment?: File | null; // Nullable
  notice: string;
}

export interface CompanySchema {
  id: string;
  nameAr: string;
  nameEn: string;
  CompanyNr: string;
  website?: string;
  contactInfo?: string;
  city?: string;
  region?: string;
}

Font.register({
  family: "Amiri",
  src: "/fonts/Amiri-Regular.ttf",
});
Font.register({
  family: "ScheherazadeNew",
  src: "/fonts/ScheherazadeNew-Regular.ttf",
});
const styles = StyleSheet.create({
  page: {
    padding: 20,
    fontFamily: "ScheherazadeNew",
    flexDirection: "column",
    backgroundColor: "#F9F9F9",
  },
  header: {
    fontSize: 20,
    fontWeight: "bold",
    marginBottom: 20,
    textAlign: "center",
    color: "#333",
    fontFamily: "ScheherazadeNew",
  },
  section: {
    marginBottom: 10,
    padding: 10,
    borderWidth: 1,
    borderColor: "#CCC",
    borderRadius: 5,
    backgroundColor: "#FFF",
  },
  sectionHeader: {
    fontSize: 14,
    fontWeight: "bold",
    marginBottom: 3,
    textAlign: "right",
    color: "#007BFF",
  },
  sectionText: {
    fontSize: 12,
    marginBottom: 5,
    textAlign: "right",
    fontFamily: "ScheherazadeNew",
    color: "#555",
  },
  table: {
    flexDirection: "column",
    width: "100%",
    marginVertical: 10,
    borderCollapse: "collapse",
  },
  tableRow: {
    flexDirection: "row-reverse",
    borderBottomWidth: 1,
    borderBottomColor: "#CCC",
    paddingVertical: 5,
  },
  tableCell: {
    flex: 1,
    fontSize: 10,
    textAlign: "right",
    padding: 5,
    color: "#333",
  },
  footer: {
    marginTop: 20,
    textAlign: "center",
    fontSize: 10,
    color: "#777",
  },
  row: {
    flexDirection: "row-reverse", 
    alignItems: "center",
    marginBottom: 5,
  },
});

const TenderPDF = ({ offer }: { offer: Offer }) => {
  const { locale } = useLocale();
  const { t } = useTranslate(locale);
  const products = offer.products || [];
  const totalAmount = products
    .reduce((sum, p) => sum + (p.unitPrice || 0) * p.quantity, 0)
    .toFixed(2);

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <Text style={styles.header}>{t("tenders.details")}</Text>
        <View style={styles.section} wrap={false}>
          <Text style={styles.sectionHeader}>{t("company.info")}</Text>
          <Text style={styles.sectionText}>
            {t("company.fields.name")}: {offer.company?.CompanyNr}
          </Text>
          <View style={styles.row}>
            <Text style={styles.sectionText}>
              :{t("company.fields.website")}{" "}
            </Text>
            <Text style={styles.sectionText}>{offer.company?.website}</Text>
          </View>

          <Text style={styles.sectionText}>
            {t("company.fields.city")}: {offer.company?.city}
          </Text>
          <Text style={styles.sectionText}>
            {t("company.fields.region")}: {offer.company?.region}
          </Text>
          <View style={styles.row}>
            <Text style={styles.sectionText}>
              :{t("company.fields.contactEmail")}{" "}
            </Text>
            <Text style={styles.sectionText}>{offer.company?.contactInfo}</Text>
          </View>
        </View>

        <View style={styles.section} wrap={false}>
          <Text style={styles.sectionHeader}>{t("tenders.info")}</Text>
          <Text style={styles.sectionText}>
            {t("company.fields.address")}: {offer.title}
          </Text>
          <Text style={styles.sectionText}>
            {t("tender.fields.minimumValue")}: {offer.minValue}{" "}
            {t("checkout.summary.currency")}
          </Text>
          <Text style={styles.sectionText}>
            {t("tender.value")}: {offer.value} {t("checkout.summary.currency")}
          </Text>
          <Text style={styles.sectionText}>
            {t("tenders.endDate")}: {moment(offer.endDate).format("YYYY-MM-DD")}
          </Text>
          <Text style={styles.sectionText}>
            {t("orders.fields.deliveryDate")}:{" "}
            {moment(offer.deliverDate).format("YYYY-MM-DD")}
          </Text>
          <View style={styles.row}>
            <Text style={styles.sectionText}>
              :{t("orders.fields.status")}{" "}
            </Text>
            <Text style={styles.sectionText}>{offer.status}</Text>
          </View>
          <Text style={styles.sectionText}>
            {t("item.fields.categories")}: {offer.categories.join(", ")}
          </Text>

          <Text style={styles.sectionText}>
            {t("company.fields.region")}:{" "}
            {offer?.region?.normalize("NFC") || "-"}
          </Text>
          <Text style={styles.sectionText}>
            {t("company.fields.city")}: {offer?.city?.normalize("NFC") || "-"}
          </Text>
        </View>


        <View style={styles.section} wrap={false}>
          <Text style={styles.sectionHeader}>{t("products.title")}</Text>
          <View style={styles.table}>
            <View style={styles.tableRow}>
              <Text style={styles.tableCell}>{t("tender.fields.product")}</Text>
              <Text style={styles.tableCell}>
                {t("checkout.fields.quantity")}
              </Text>
              <Text style={styles.tableCell}>
                {t("checkout.fields.unitPrice")}
              </Text>
              <Text style={styles.tableCell}>{t("tender.fields.total")}</Text>
            </View>
            {products.map((product, index) => (
              <View style={styles.tableRow} key={index}>
                <Text style={styles.tableCell}>
                  {product.item?.normalize("NFC")}
                </Text>
                <Text style={styles.tableCell}>{product.quantity}</Text>
                <Text style={styles.tableCell}>
                  {product.unitPrice?.toFixed(2) || "-"}
                </Text>
                <Text style={styles.tableCell}>
                  {(product.unitPrice || 0) * product.quantity}
                </Text>
              </View>
            ))}
          </View>
          <Text style={styles.sectionText}>
            الإجمالي: {totalAmount} {t("checkout.summary.currency")}
          </Text>
        </View>
      </Page>
    </Document>
  );
};

export const generatePDFWithForm = async (offer: Offer) => {
  const blob = await pdf(<TenderPDF offer={offer} />).toBlob();
  const url = URL.createObjectURL(blob);

  const link = document.createElement("a");
  link.href = url;
  link.download = "tender-info.pdf";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};
